import { useState } from 'react'
import axios from 'axios'

import { API_BASE_URL } from '../config/api'

export const useFileUpload = (eventId, onUploadSuccess, onError) => {
  const [files, setFiles] = useState([])
  const [uploading, setUploading] = useState(false)

  const handleFiles = (selectedFiles) => {
    const fileArray = Array.from(selectedFiles)
    const validFiles = fileArray.filter(file => {
      const isValidType = file.type.match(/^(image|video)\//)
      const isValidSize = file.size <= 50 * 1024 * 1024 // 50MB
      return isValidType && isValidSize
    })

    if (validFiles.length !== fileArray.length) {
      onError('Some files were skipped. Only images and videos under 50MB are allowed.')
    }

    const filesWithPreview = validFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      description: '',
      customName: file.name.replace(/\.[^/.]+$/, "") // Remove file extension for default name
    }))

    setFiles(prev => [...prev, ...filesWithPreview])
  }

  const updateDescription = (fileId, description) => {
    setFiles(prev => prev.map(f =>
      f.id === fileId ? { ...f, description } : f
    ))
  }

  const updateCustomName = (fileId, customName) => {
    setFiles(prev => prev.map(f =>
      f.id === fileId ? { ...f, customName } : f
    ))
  }

  const removeFile = (fileId) => {
    setFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId)
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview)
      }
      return prev.filter(f => f.id !== fileId)
    })
  }

  const updateFilePreview = (fileId, newPreviewData) => {
    setFiles(prev => prev.map(f => {
      if (f.id === fileId) {
        fetch(newPreviewData)
          .then(res => res.blob())
          .then(blob => {
            const newFile = new File([blob], f.file.name, { type: f.file.type })
            setFiles(prevFiles => prevFiles.map(prevF =>
              prevF.id === fileId ? { ...prevF, file: newFile, preview: newPreviewData } : prevF
            ))
          })
        return { ...f, preview: newPreviewData }
      }
      return f
    }))
  }

  const uploadFiles = async () => {
    if (files.length === 0) return

    // Check if eventId is valid
    if (!eventId || eventId === 'undefined' || eventId === null) {
      onError('Event ID is missing. Please refresh the page and try again.')
      return
    }

    setUploading(true)

    try {
      const formData = new FormData()
      files.forEach((fileObj, index) => {
        formData.append('media', fileObj.file)
        formData.append('descriptions', fileObj.description)
        formData.append('customNames', fileObj.customName)
      })

      const response = await axios.post(
        `${API_BASE_URL}/api/events/${eventId}/media`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true
        }
      )

      // Clean up preview URLs
      files.forEach(fileObj => {
        if (fileObj.preview) {
          URL.revokeObjectURL(fileObj.preview)
        }
      })

      setFiles([])
      onUploadSuccess(response.data)

    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Upload error:', error)
      onError(error.response?.data?.message || 'Failed to upload media')
    } finally {
      setUploading(false)
    }
  }

  return {
    files,
    uploading,
    handleFiles,
    updateDescription,
    updateCustomName,
    removeFile,
    updateFilePreview,
    uploadFiles
  }
}